import type { AxiosError, AxiosRequestConfig } from "axios";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "sonner";

import { formatLongDate } from "@/utils/date-utils";

import { env } from "@/configs/env";
import i18n from "@/configs/i18n";
import type { ApiResponse, Auth } from "@/types";

import { ERROR_TOAST_DURATION, PARAMETERISED_ERRORS, REQUEST_ID_HEADER } from "./constants";
import { shownErrors } from "./state";
import type { ApiErrorEnvelope, ErrorResponse } from "./types";

/**
 * `requestId` ni toast'ning XIRA pastki qatoriga qo'yadi.
 *
 * U xato KODI emas — so'rovning jurnaldagi raqami, shuning uchun asosiy
 * matnga aralashmaydi: oddiy foydalanuvchi uni umuman o'qimasa ham
 * bo'ladi, shikoyat qilgan odam esa nusxalab bera oladi va biz butun
 * so'rovni jurnaldan tiklaymiz.
 */
const requestIdToastOptions = (requestId: string) => ({
  description: i18n.t("COMMON.REQUEST_ID", { id: requestId }),
  action: {
    label: i18n.t("COMMON.COPY"),
    onClick: () => {
      // Clipboard API HTTPS'siz kontekstda yoki ruxsatsiz yiqilishi
      // mumkin. Bu qulaylik, ish emas — jimgina o'tkazib yuboriladi.
      void navigator.clipboard?.writeText(requestId).catch(() => {});
    },
  },
});

export const showErrorToast = (message: string, requestId?: string) => {
  if (shownErrors.has(message)) return;

  shownErrors.add(message);
  if (requestId) toast.error(message, requestIdToastOptions(requestId));
  else toast.error(message);
  setTimeout(() => shownErrors.delete(message), ERROR_TOAST_DURATION);
};

/**
 * Xato tanasidan ichma-ich `error` konvertini ajratadi.
 *
 * Yangi shakl — `{ error: { code, message, details }, requestId, ... }`.
 * Eski yassi shakl (`{ code, message }`) ham qabul qilinadi: eski
 * o'rnatmalar va o'z proksilarimiz hali shunday javob berishi mumkin, va
 * ularda ham toast bo'sh qolmasligi kerak.
 */
export const getErrorEnvelope = (error: AxiosError): ApiErrorEnvelope | undefined => {
  const data = error.response?.data as ErrorResponse | undefined;
  if (!data) return undefined;

  const nested = data.error;
  if (nested && typeof nested === "object" && typeof nested.code === "string") return nested;

  // Eski konvert: `code` yassi turadi, `error` esa HTTP holat nomi
  // (`"Bad Request"`) bo'lishi mumkin — u kod EMAS.
  if (typeof data.code === "string" && data.code) {
    return {
      code: data.code,
      message: Array.isArray(data.message) ? data.message[0] : data.message,
    };
  }
  return undefined;
};

/** Backend bergan barqaror xato kodi (`ERRORS.<CODE>` kaliti bilan bir xil). */
export const getErrorCode = (error: AxiosError): string | undefined =>
  getErrorEnvelope(error)?.code;

/**
 * So'rovning jurnaldagi raqami.
 *
 * Backend uni HAM `X-Request-Id` sarlavhasida, HAM tanada qaytaradi.
 * Sarlavha birinchi: tana JSON bo'lmagan holatda (proksi 502 sahifasi)
 * faqat u qoladi.
 */
export const getRequestId = (error: AxiosError): string | undefined => {
  const header = error.response?.headers?.[REQUEST_ID_HEADER];
  if (typeof header === "string" && header) return header;

  const data = error.response?.data as ErrorResponse | undefined;
  return typeof data?.requestId === "string" && data.requestId ? data.requestId : undefined;
};

/**
 * `errors` — `class-validator` ning INGLIZ matnlari (ADR 0002 bo'yicha
 * tarjima qilinmaydi). U interpolyatsiyaga umuman uzatilmaydi: lug'atdagi
 * biror xabar tasodifan `{{errors}}` yozib qo'ysa ham, ekranga dasturchi
 * matni chiqmaydi.
 */
const SKIPPED_DETAIL_KEYS = new Set(["errors"]);

/** `2026-09-01T05:53:23.619Z` — backend sanalarni aynan shu shaklda beradi. */
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}|$)/;

const BYTES_IN_MB = 1024 * 1024;

/**
 * ISO sanani foydalanuvchi tilidagi ko'rinishga o'tkazadi.
 *
 * Bu TARJIMA emas, formatlash: xom holda `{{dueDate}}` o'rniga ekranda
 * "2026-09-01T05:53:23.619Z" turardi. `Intl` emas, dayjs lokali: o'zbekcha
 * ICU ma'lumotisiz brauzerda `Intl` "M09 1, 2026" chiqarardi.
 */
const formatDateTime = (value: string): string => {
  const date = dayjs(value);
  if (!date.isValid()) return value;
  return `${formatLongDate(value, i18n.language)}, ${date.format("HH:mm")}`;
};

/** "10 MB" — o'lchov birligi ham tilga qarab yoziladi (ru: "10 МБ"). */
const formatMegabytes = (megabytes: number): string => {
  const rounded = Math.round(megabytes * 10) / 10;
  try {
    return new Intl.NumberFormat(i18n.language, {
      style: "unit",
      unit: "megabyte",
      unitDisplay: "short",
    }).format(rounded);
  } catch {
    return `${rounded} MB`;
  }
};

/**
 * `FILE_TOO_LARGE` uchta joydan keladi va uchalasi boshqacha yozadi:
 * pipe tayyor yorliq (`maxSize: "10 MB"`), topshiriq megabayt soni
 * (`maxSizeMb`), uploads esa baytlar (`maxBytes`). Lug'atda esa bitta
 * xabar bo'lishi kerak, shuning uchun uchalasi shu yerda BITTA
 * `maxSizeLabel` ga keltiriladi.
 */
const toSizeLabel = (details: Record<string, unknown>): string | undefined => {
  if (typeof details.maxSize === "string" && details.maxSize) return details.maxSize;
  if (typeof details.maxSizeMb === "number") return formatMegabytes(details.maxSizeMb);
  if (typeof details.maxBytes === "number") return formatMegabytes(details.maxBytes / BYTES_IN_MB);
  return undefined;
};

/**
 * `details` dan interpolyatsiyaga uzatiladigan qiymatlarni ajratadi.
 *
 * Massivlar (`fields`, `allowed`) o'rin egallovchiga to'g'ridan-to'g'ri
 * qo'yilmaydi — ular vergul bilan birlashtiriladi va yoniga `<kalit>Count`
 * qo'shiladi, ya'ni xabar "3 ta maydon" deb ham, "pdf, docx" deb ham
 * yozilishi mumkin.
 */
export const toMessageValues = (
  details: Record<string, unknown> | null | undefined,
): Record<string, string | number> => {
  const values: Record<string, string | number> = {};
  if (!details) return values;

  for (const [key, value] of Object.entries(details)) {
    if (SKIPPED_DETAIL_KEYS.has(key)) continue;

    if (typeof value === "number") {
      values[key] = value;
    } else if (typeof value === "string") {
      values[key] = ISO_DATE_TIME.test(value) ? formatDateTime(value) : value;
    } else if (Array.isArray(value)) {
      const items = value.filter(
        (item): item is string | number => typeof item === "string" || typeof item === "number",
      );
      // Bo'sh ro'yxat parametrli xabarni "0 ta maydonni tekshiring" ga
      // aylantirardi — bunday holatda oddiy xabar to'g'riroq.
      if (items.length === 0) continue;
      values[key] = items.join(", ");
      values[`${key}Count`] = items.length;
    }
  }

  const sizeLabel = toSizeLabel(details);
  if (sizeLabel) values.maxSizeLabel = sizeLabel;

  return values;
};

/** Lug'atda yo'q kalit uchun `t` kalitning O'ZINI qaytaradi — u matn emas. */
const translateOrNothing = (
  key: string,
  values?: Record<string, string | number>,
): string | undefined => {
  const text = i18n.t(key, values);
  return text && text !== key ? text : undefined;
};

/**
 * Foydalanuvchiga ko'rsatiladigan matn.
 *
 * Tartib QAT'IY: kod → lug'at. Backend'ning `message` i tarjima qilinmagan
 * ingliz matni va u UI ga CHIQMAYDI — noma'lum kod ham umumiy xabar
 * beradi, "quiz has attempts — the answer key is frozen" emas.
 *
 * Parametrli variant (`ERROR_DETAILS.<CODE>`) faqat `details` da uning
 * TALAB QILGAN qiymatlari bo'lganda ishlatiladi; aks holda hujjatdagi
 * oddiy xabarga qaytiladi. Ya'ni yarim to'ldirilgan gap ("Eng ko'pi ta")
 * hech qachon ekranga chiqmaydi.
 */
export const getErrorMessage = (error: AxiosError): string => {
  if (!error.response) {
    return error.request ? i18n.t("COMMON.NETWORK_ERROR") : i18n.t("COMMON.UNKNOWN_ERROR");
  }

  const envelope = getErrorEnvelope(error);
  if (envelope) {
    const values = toMessageValues(envelope.details);
    const required = PARAMETERISED_ERRORS[envelope.code];

    if (required?.every(name => name in values)) {
      const detailed = translateOrNothing(`ERROR_DETAILS.${envelope.code}`, values);
      if (detailed) return detailed;
    }

    const plain = translateOrNothing(`ERRORS.${envelope.code}`);
    if (plain) return plain;
  }

  // Lug'atda yo'q kod — 195 tasidan biri emas yoki backend yangiroq.
  // Ekranda umumiy xabar turadi; aniq kod konsolda qoladi.
  return i18n.t("COMMON.ERROR");
};

/**
 * Yangi access token oladi.
 *
 * Tana BO'SH: refresh token `lu_rt` HttpOnly cookie'sida va brauzer uni
 * o'zi biriktiradi (`withCredentials`). Ilgari token bu yerga
 * `localStorage` dan kelardi — ya'ni bitta XSS 30 kunlik hisob
 * ma'lumotini olib chiqa olardi.
 *
 * Umumiy nusxa emas, yalang'och axios chaqiruvi ishlatiladi: 401 hal
 * qilinayotgan paytda javob interceptoriga qayta kirib ketmaslik uchun.
 */
export const refreshAuthToken = () => {
  return axios.post<ApiResponse<Auth>>(
    `${env.API_BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true },
  );
};

export const updateRequestAuth = (config: AxiosRequestConfig, token: string) => {
  config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  return config;
};
